
// Deployment trigger: 2026-04-24
import React, { useState } from 'react';
import { 
  Gift, 
  Plus, 
  Trash2, 
  Save, 
  AlertCircle,
  Layout,
  Mail,
  Zap,
  CheckCircle2,
  Send,
  Loader2,
  MessageSquare,
  Smartphone,
  Gamepad2
} from 'lucide-react';
import { Prize, GlobalConfig, GameType } from '../types';
import { GAME_TEMPLATES, DEFAULT_ENABLED_GAMES } from '../constants';
import { sendBrevoEmail } from '../services/emailService';
import { sendSignalWireSms } from '../services/smsService';
import UserManagement from './UserManagement';

interface GlobalSettingsProps {
  config: GlobalConfig;
  onUpdate: (config: GlobalConfig) => void | Promise<void>;
  userRole?: string;
}

const GlobalSettings: React.FC<GlobalSettingsProps> = ({ config, onUpdate, userRole }) => {
  const [localConfig, setLocalConfig] = useState<GlobalConfig>({...config});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  
  React.useEffect(() => {
    setLocalConfig({...config});
  }, [config]);

  // Email Testing State
  const [testRecipient, setTestRecipient] = useState('');
  const [testEmailStatus, setTestEmailStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [testEmailError, setTestEmailError] = useState<string | null>(null);

  // SMS Testing State
  const [testPhone, setTestPhone] = useState('');
  const [testSmsStatus, setTestSmsStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [testSmsError, setTestSmsError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      await onUpdate(localConfig);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setSaveStatus('idle');
      console.error("Failed to save:", err);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testRecipient) return;
    setTestEmailStatus('sending');
    setTestEmailError(null);

    try {
      const result = await sendBrevoEmail({
        toEmail: testRecipient,
        toName: 'Test User',
        subject: `🚀 ${localConfig.platformName || 'Business Name'}: Connection Test`,
        prizeName: 'Test Reward 100%',
        campaignName: 'System Configuration Check',
        isRaffle: false,
        fromName: localConfig.emailSenderName || localConfig.platformName || 'Business Name'
      });

      if (result) {
        setTestEmailStatus('success');
        setTimeout(() => setTestEmailStatus('idle'), 5000);
      }
    } catch (err: any) {
      setTestEmailStatus('error');
      setTestEmailError(err.message || "Failed to connect to email server.");
    }
  };

  const handleSendTestSms = async () => {
    if (!testPhone) return;
    setTestSmsStatus('sending');
    setTestSmsError(null);

    try {
      await sendSignalWireSms({
        toPhone: testPhone,
        message: `Your ${localConfig.platformName || 'Business Name'} test code is: 123456. Your SMS system is correctly configured.`
      });
      setTestSmsStatus('success');
      setTimeout(() => setTestSmsStatus('idle'), 5000);
    } catch (err: any) {
      setTestSmsStatus('error');
      setTestSmsError(err.message || "Failed to connect to SignalWire.");
    }
  };

  const updatePrize = (index: number, updates: Partial<Prize>) => {
    const newPrizes = [...localConfig.defaultPrizes];
    newPrizes[index] = { ...newPrizes[index], ...updates };
    setLocalConfig({ ...localConfig, defaultPrizes: newPrizes });
  };

  const addPrize = () => {
    const newPrize: Prize = {
      id: `def-${Date.now()}`,
      name: 'New Default Reward',
      type: 'coupon',
      value: 'WELCOME10',
      probability: 20,
      color: '#6366f1'
    };
    setLocalConfig({ ...localConfig, defaultPrizes: [...localConfig.defaultPrizes, newPrize] });
  };

  const removePrize = (id: string) => {
    setLocalConfig({ ...localConfig, defaultPrizes: localConfig.defaultPrizes.filter(p => p.id !== id) });
  };

  const totalWeight = localConfig.defaultPrizes.reduce((sum, p) => sum + (Number(p.probability) || 0), 0);

  const updateRafflePrize = (index: number, updates: Partial<Prize>) => {
    const newPrizes = [...(localConfig.defaultRafflePrizes || [])];
    newPrizes[index] = { ...newPrizes[index], ...updates };
    setLocalConfig({ ...localConfig, defaultRafflePrizes: newPrizes });
  };

  const addRafflePrize = () => {
    const newPrize: Prize = {
      id: `def-raf-${Date.now()}`,
      name: 'New Raffle Prize',
      type: 'physical',
      value: 'PRIZE',
      probability: 10,
      color: '#6366f1',
      winnersCount: 1
    };
    setLocalConfig({ ...localConfig, defaultRafflePrizes: [...(localConfig.defaultRafflePrizes || []), newPrize] });
  };

  const removeRafflePrize = (id: string) => {
    setLocalConfig({ ...localConfig, defaultRafflePrizes: (localConfig.defaultRafflePrizes || []).filter(p => p.id !== id) });
  };

  const totalRaffleWeight = (localConfig.defaultRafflePrizes || []).reduce((sum, p) => sum + (Number(p.probability) || 0), 0);

  // Which game engines users are allowed to build campaigns with.
  const enabledGames = localConfig.enabledGames ?? DEFAULT_ENABLED_GAMES;
  // Toggling a game persists immediately (it's a switch, not a draft field) so
  // the choice survives a refresh without needing the master Save button.
  const toggleGame = async (id: GameType) => {
    const next = enabledGames.includes(id)
      ? enabledGames.filter(g => g !== id)
      : [...enabledGames, id];
    if (next.length === 0) return; // keep at least one game enabled
    const updated = { ...localConfig, enabledGames: next };
    setLocalConfig(updated);
    setSaveStatus('saving');
    try {
      await onUpdate(updated);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      setSaveStatus('idle');
      console.error('Failed to save game availability:', err);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Basic Config */}
        <div className="lg:col-span-1 space-y-8">
          <div className="bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm">
             <div className="flex items-center gap-3 mb-8">
                <div className="p-2.5 bg-indigo-50 rounded-2xl">
                  <Layout className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">General Config</h3>
             </div>

             <div className="space-y-6">
                <div>
                   <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Platform Identity</label>
                   <input 
                      type="text" 
                      value={localConfig.platformName}
                      onChange={e => setLocalConfig({...localConfig, platformName: e.target.value})}
                      className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50/50 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all font-bold shadow-inner"
                   />
                </div>
                <div>
                   <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Admin Support Email</label>
                   <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input 
                        type="email" 
                        value={localConfig.supportEmail}
                        onChange={e => setLocalConfig({...localConfig, supportEmail: e.target.value})}
                        className="w-full pl-12 pr-5 py-4 rounded-2xl border border-slate-200 bg-slate-50/50 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all font-bold shadow-inner"
                      />
                   </div>
                </div>
                                <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
                   <span className="text-xs font-bold text-slate-700">Advanced Analytics</span>
                   <button 
                      onClick={() => setLocalConfig({...localConfig, enableAnalytics: !localConfig.enableAnalytics})}
                      className={`w-12 h-6 rounded-full transition-all relative ${localConfig.enableAnalytics ? 'bg-indigo-600' : 'bg-slate-200'}`}
                   >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${localConfig.enableAnalytics ? 'left-7' : 'left-1'}`} />
                   </button>
                </div>

                <div className="pt-6 border-t border-slate-100">
                  <label className="text-slate-700 font-bold text-xs block mb-2 uppercase tracking-widest flex items-center justify-between">
                    <span>Image Compression Quality</span>
                    <span className="text-indigo-500">{localConfig.imageCompression ?? 80}%</span>
                  </label>
                  <div className="relative group h-[58px] flex items-center bg-white border border-slate-200 rounded-2xl px-6 shadow-sm">
                    <input 
                      type="range" 
                      min="10"
                      max="100"
                      step="5"
                      value={localConfig.imageCompression ?? 80}
                      onChange={(e) => setLocalConfig({...localConfig, imageCompression: Number(e.target.value)})}
                      className="w-full accent-indigo-600 cursor-pointer"
                    />
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium mt-2">
                    Lower values upload faster but reduce image quality. Default is 80%.
                  </p>
                </div>
             </div>
          </div>

          {/* Available Games */}
          <div className="bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm">
             <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-indigo-50 rounded-2xl">
                  <Gamepad2 className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">Available Games</h3>
                {saveStatus === 'saving' && (
                  <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin ml-auto" />
                )}
                {saveStatus === 'saved' && (
                  <span className="ml-auto flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-500">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Saved
                  </span>
                )}
             </div>

             <p className="text-[10px] text-slate-500 font-medium mb-6 leading-relaxed">
               Choose which game engines your team can pick when creating campaigns. Changes save automatically.
             </p>

             <div className="space-y-4">
                {GAME_TEMPLATES.map(game => {
                  const on = enabledGames.includes(game.id);
                  return (
                    <div key={game.id} className="flex items-center justify-between gap-4">
                       <div className="min-w-0">
                          <div className="text-xs font-black text-slate-700">{game.name}</div>
                          <div className="text-[10px] text-slate-400 font-medium truncate">{game.description}</div>
                       </div>
                       <button
                          type="button"
                          onClick={() => toggleGame(game.id)}
                          aria-pressed={on}
                          className={`w-12 h-6 rounded-full transition-all relative shrink-0 ${on ? 'bg-indigo-600' : 'bg-slate-200'}`}
                       >
                          <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${on ? 'left-7' : 'left-1'}`} />
                       </button>
                    </div>
                  );
                })}
             </div>
          </div>

          {/* Email & SMS Templates */}
          <div className="bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm">
             <div className="flex items-center gap-3 mb-8">
                <div className="p-2.5 bg-indigo-50 rounded-2xl">
                  <MessageSquare className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">Messaging Templates</h3>
             </div>
             
             <div className="mb-6 p-4 bg-slate-50 rounded-2xl border border-slate-100">
               <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-2">Available Shortcodes:</p>
               <div className="flex flex-wrap gap-2">
                 {['{BusinessName}', '{GameName}', '{PrizeName}', '{TicketNumber}', '{CampaignCode}'].map(code => (
                   <span key={code} className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-mono text-indigo-600 font-bold">
                     {code}
                   </span>
                 ))}
               </div>
             </div>

             <div className="space-y-6">
                <div>
                   <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Email Sender Name</label>
                   <input 
                      type="text" 
                      value={localConfig.emailSenderName ?? localConfig.platformName}
                      onChange={e => setLocalConfig({...localConfig, emailSenderName: e.target.value})}
                      placeholder="e.g. My Awesome Business"
                      className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50/50 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all font-bold shadow-inner"
                   />
                </div>
                <div>
                   <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Email Subject Template</label>
                   <input 
                      type="text" 
                      value={localConfig.emailSubjectTemplate ?? '🎉 You won {PrizeName} from {BusinessName}!'}
                      onChange={e => setLocalConfig({...localConfig, emailSubjectTemplate: e.target.value})}
                      placeholder="🎉 You won {PrizeName} from {BusinessName}!"
                      className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50/50 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all font-bold shadow-inner"
                   />
                </div>
                <div>
                   <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Email Content Template</label>
                   <textarea 
                      value={localConfig.emailContentTemplate ?? 'Congratulations! You have won {PrizeName} in our {GameName} campaign.\n\nYour ticket number is: {TicketNumber}\nCampaign Code: {CampaignCode}\n\nThanks for playing!'}
                      onChange={e => setLocalConfig({...localConfig, emailContentTemplate: e.target.value})}
                      rows={5}
                      placeholder="Congratulations! You have won {PrizeName}..."
                      className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50/50 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all font-bold shadow-inner resize-none"
                   />
                </div>
                <div>
                   <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">SMS Content Template</label>
                   <textarea 
                      value={localConfig.smsContentTemplate ?? 'Congrats! You won {PrizeName} from {BusinessName}. Ticket: {TicketNumber}. Code: {CampaignCode}'}
                      onChange={e => setLocalConfig({...localConfig, smsContentTemplate: e.target.value})}
                      rows={3}
                      placeholder="Congrats! You won {PrizeName}..."
                      className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50/50 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all font-bold shadow-inner resize-none"
                   />
                </div>
             </div>
          </div>

          {/* Email Test Card */}
          <div className="bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm overflow-hidden relative group">
             <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-amber-50 rounded-2xl">
                  <Send className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">Email Server Test</h3>
             </div>
             
             <p className="text-[10px] text-slate-500 font-medium mb-6 leading-relaxed">
               Ensure your <span className="text-slate-900 font-bold">Cloudflare Variables</span> are correctly set to connect with Brevo.
             </p>

             <div className="space-y-4">
                <input 
                   type="email" 
                   placeholder="test@example.com"
                   value={testRecipient}
                   onChange={e => setTestRecipient(e.target.value)}
                   className="w-full px-5 py-3.5 rounded-xl border border-slate-200 bg-slate-50/30 text-xs font-bold focus:bg-white outline-none transition-all"
                />
                <button 
                  disabled={testEmailStatus === 'sending' || !testRecipient}
                  onClick={handleSendTestEmail}
                  className={`w-full py-4 rounded-xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 transition-all shadow-lg ${
                    testEmailStatus === 'sending' ? 'bg-slate-100 text-slate-400' :
                    testEmailStatus === 'success' ? 'bg-emerald-500 text-white shadow-emerald-200' :
                    testEmailStatus === 'error' ? 'bg-red-500 text-white shadow-red-200' :
                    'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {testEmailStatus === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                   testEmailStatus === 'success' ? <CheckCircle2 className="w-4 h-4" /> : 
                   <Send className="w-3.5 h-3.5" />}
                  {testEmailStatus === 'sending' ? 'Firing...' : 
                   testEmailStatus === 'success' ? 'Email Sent' : 
                   testEmailStatus === 'error' ? 'Failed' : 'Run Email Test'}
                </button>
                
                {testEmailError && (
                  <div className="p-3 bg-red-50 rounded-xl border border-red-100 flex items-start gap-2 mt-2">
                    <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-[9px] text-red-600 leading-tight font-bold">{testEmailError}</p>
                  </div>
                )}
             </div>
          </div>

          {/* SMS Test Card */}
          <div className="bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm overflow-hidden relative group">
             <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-indigo-50 rounded-2xl">
                  <Smartphone className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">SignalWire SMS Test</h3>
             </div>
             
             <p className="text-[10px] text-slate-500 font-medium mb-6 leading-relaxed">
               Verify your <span className="text-indigo-600 font-bold">SignalWire Space</span> connection and API credentials.
             </p>

             <div className="space-y-4">
                <div className="relative">
                  <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input 
                    type="tel" 
                    placeholder="+1234567890"
                    value={testPhone}
                    onChange={e => setTestPhone(e.target.value)}
                    className="w-full pl-12 pr-5 py-3.5 rounded-xl border border-slate-200 bg-slate-50/30 text-xs font-bold focus:bg-white outline-none transition-all"
                  />
                </div>
                <button 
                  disabled={testSmsStatus === 'sending' || !testPhone}
                  onClick={handleSendTestSms}
                  className={`w-full py-4 rounded-xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 transition-all shadow-lg ${
                    testSmsStatus === 'sending' ? 'bg-slate-100 text-slate-400' :
                    testSmsStatus === 'success' ? 'bg-indigo-500 text-white shadow-indigo-200' :
                    testSmsStatus === 'error' ? 'bg-red-500 text-white shadow-red-200' :
                    'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {testSmsStatus === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                   testSmsStatus === 'success' ? <CheckCircle2 className="w-4 h-4" /> : 
                   <MessageSquare className="w-3.5 h-3.5" />}
                  {testSmsStatus === 'sending' ? 'Sending SMS...' : 
                   testSmsStatus === 'success' ? 'SMS Delivered' : 
                   testSmsStatus === 'error' ? 'Failed' : 'Run SMS Test'}
                </button>
                
                {testSmsError && (
                  <div className="p-3 bg-red-50 rounded-xl border border-red-100 flex items-start gap-2 mt-2">
                    <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-[9px] text-red-600 leading-tight font-bold">{testSmsError}</p>
                  </div>
                )}
             </div>
          </div>
        </div>

        {/* Default Prize Editor */}
        <div className="lg:col-span-2 space-y-8">
           <div className="bg-white p-8 sm:p-12 rounded-[40px] border border-slate-200 shadow-sm">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-12">
                 <div className="flex items-center gap-4">
                    <div className="p-3 bg-emerald-50 rounded-2xl">
                      <Gift className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">Standard Rewards Library</h3>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Default template for new games</p>
                    </div>
                 </div>
                 
                 <button 
                    onClick={addPrize}
                    className="flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg"
                 >
                    <Plus className="w-4 h-4" /> Add Reward
                 </button>
              </div>

              {totalWeight > 100 && (
                <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 animate-in slide-in-from-top-2">
                   <AlertCircle className="w-5 h-5" />
                   <span className="text-[10px] font-black uppercase tracking-widest">Total Weight is {totalWeight}% (Ideal: 100%)</span>
                </div>
              )}

              <div className="space-y-4">
                 {localConfig.defaultPrizes.map((prize, idx) => (
                    <div key={prize.id} className="group p-5 bg-slate-50/50 rounded-3xl border border-slate-100 hover:border-indigo-200 hover:bg-white transition-all flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                       <div className="flex items-center gap-4 flex-1">
                          <div className="w-8 h-8 rounded-xl bg-white shadow-sm flex items-center justify-center font-black text-[10px] text-slate-400 border border-slate-100">{idx + 1}</div>
                          <input 
                             type="text"
                             value={prize.name}
                             onChange={e => updatePrize(idx, {name: e.target.value})}
                             className="bg-transparent font-black text-slate-800 text-sm focus:outline-none border-b border-transparent focus:border-indigo-400 flex-1 px-1"
                             placeholder="Reward name"
                          />
                       </div>

                       <div className="flex items-center gap-4">
                          <div className="w-20">
                             <span className="text-[8px] font-black text-slate-400 block mb-1 uppercase tracking-widest ml-1">Weight</span>
                             <input 
                                type="number"
                                value={prize.probability}
                                onChange={e => updatePrize(idx, {probability: Number(e.target.value)})}
                                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-black text-indigo-600 text-center outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all"
                             />
                          </div>
                          <div className="w-32">
                             <span className="text-[8px] font-black text-slate-400 block mb-1 uppercase tracking-widest ml-1">Type</span>
                             <select 
                                value={prize.type}
                                onChange={e => updatePrize(idx, {type: e.target.value as any})}
                                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-black uppercase outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all cursor-pointer"
                             >
                                <option value="coupon">Coupon</option>
                                <option value="physical">Physical</option>
                                <option value="digital">Digital</option>
                                <option value="empty">Empty</option>
                             </select>
                          </div>
                          <button 
                             onClick={() => removePrize(prize.id)}
                             className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all self-end mb-1"
                          >
                             <Trash2 className="w-4 h-4" />
                          </button>
                       </div>
                    </div>
                 ))}
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mt-16 mb-12">
                 <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-50 rounded-2xl">
                      <Gift className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">Raffle Rewards Library</h3>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Default template for new raffles</p>
                    </div>
                 </div>
                 
                 <button 
                    onClick={addRafflePrize}
                    className="flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg"
                 >
                    <Plus className="w-4 h-4" /> Add Reward
                 </button>
              </div>

              {totalRaffleWeight > 100 && (
                <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 animate-in slide-in-from-top-2">
                   <AlertCircle className="w-5 h-5" />
                   <span className="text-[10px] font-black uppercase tracking-widest">Total Weight is {totalRaffleWeight}% (Ideal: 100%)</span>
                </div>
              )}

              <div className="space-y-4">
                 {(localConfig.defaultRafflePrizes || []).map((prize, idx) => (
                    <div key={prize.id} className="group p-5 bg-slate-50/50 rounded-3xl border border-slate-100 hover:border-indigo-200 hover:bg-white transition-all flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                       <div className="flex items-center gap-4 flex-1">
                          <div className="w-8 h-8 rounded-xl bg-white shadow-sm flex items-center justify-center font-black text-[10px] text-slate-400 border border-slate-100">{idx + 1}</div>
                          <input 
                             type="text"
                             value={prize.name}
                             onChange={e => updateRafflePrize(idx, {name: e.target.value})}
                             className="bg-transparent font-black text-slate-800 text-sm focus:outline-none border-b border-transparent focus:border-indigo-400 flex-1 px-1"
                             placeholder="Reward name"
                          />
                       </div>

                       <div className="flex items-center gap-4">
                          <div className="w-20">
                             <span className="text-[8px] font-black text-slate-400 block mb-1 uppercase tracking-widest ml-1">Weight</span>
                             <input 
                                type="number"
                                value={prize.probability}
                                onChange={e => updateRafflePrize(idx, {probability: Number(e.target.value)})}
                                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-black text-indigo-600 text-center outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all"
                             />
                          </div>
                          <div className="w-32">
                             <span className="text-[8px] font-black text-slate-400 block mb-1 uppercase tracking-widest ml-1">Type</span>
                             <select 
                                value={prize.type}
                                onChange={e => updateRafflePrize(idx, {type: e.target.value as any})}
                                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-black uppercase outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all cursor-pointer"
                             >
                                <option value="coupon">Coupon</option>
                                <option value="physical">Physical</option>
                                <option value="digital">Digital</option>
                                <option value="empty">Empty</option>
                             </select>
                          </div>
                          <button 
                             onClick={() => removeRafflePrize(prize.id)}
                             className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all self-end mb-1"
                          >
                             <Trash2 className="w-4 h-4" />
                          </button>
                       </div>
                    </div>
                 ))}
              </div>

              <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-slate-100">
                 <div className="flex items-center gap-4">
                    {saveStatus === 'saved' && (
                       <div className="flex items-center gap-2 text-emerald-500 animate-in fade-in slide-in-from-left-2">
                          <CheckCircle2 className="w-5 h-5" />
                          <span className="text-[10px] font-black uppercase tracking-widest">Global settings updated</span>
                       </div>
                    )}
                 </div>
                 
                 <button 
                    onClick={handleSave}
                    disabled={saveStatus === 'saving' || totalWeight > 100}
                    className="flex items-center gap-3 bg-indigo-600 text-white px-10 py-4 rounded-2xl font-black uppercase tracking-[0.2em] text-xs hover:bg-indigo-700 transition-all disabled:opacity-50 shadow-xl shadow-indigo-100"
                 >
                    {saveStatus === 'saving' ? (
                       <span className="animate-pulse">Processing...</span>
                    ) : (
                       <><Save className="w-4 h-4" /> Save Master Config</>
                    )}
                 </button>
              </div>
           </div>

           {(userRole === 'admin' || userRole === 'moderator') && (
             <UserManagement currentRole={userRole as 'admin' | 'moderator'} />
           )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSettings;
